import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { format, isValid } from 'date-fns';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';

import { usePublishTradingOfferModal } from '../context/PublishTradingOfferModalContext';

const PublishTradingOfferModalStep1: FC = () => {
  const { t } = useTranslate();
  const { data } = usePublishTradingOfferModal();

  const isSellOffer = data.tradingOffer?.type === 'SELL';
  const className = data.tradingOffer?.className || '';
  const sharesCount = data.tradingOffer?.sharesCount?.total || 0;
  const sharePrice = data.tradingOffer?.sharePrice || 0;
  const totalOfferPrice = sharePrice * sharesCount;

  const offerTypeLabel = isSellOffer ? t('Sale') : t('Buy');

  const partialAcceptanceLabel = data.tradingOffer?.isPartialAmountEnabled
    ? t('Yes')
    : t('Nope');

  const formatDateValue = (date?: string | Date) => {
    if (!date || !isValid(new Date(date))) return '-';
    return format(new Date(date), dateFormat);
  };

  const validityLabel = `${formatDateValue(
    data.tradingOffer?.startDate
  )} – ${formatDateValue(data.tradingOffer?.endDate)}`;

  return (
    <div className="nk-stepper-step active">
      <div className="text-center mb-4">
        <em className="icon ni ni-shield-check fs-24px text-primary" />
        <h5 className="mt-2">{t('ConfirmYourOfferDetails')}</h5>
        <p className="text-soft">{t('ReviewCarefullyBeforeConfirming')}</p>
      </div>

      <div className="card card-bordered bg-light">
        <div className="card-inner">
          <div className="row">
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('OfferType')}
              </span>
              <strong>{offerTypeLabel}</strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('ShareClass')}
              </span>
              <strong>{className || '-'}</strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('SharesNumber')}
              </span>
              <strong className="text-primary">
                {formatNumber(sharesCount)} {t('Shares').toLowerCase()}
              </strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('PricePerShare')}
              </span>
              <strong>{formatCurrencyDecimals(sharePrice)}</strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('TotalAmount')}
              </span>
              <strong className="text-primary fs-16px">
                {formatCurrencyDecimals(totalOfferPrice)}
              </strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">{t('Validity')}</span>
              <strong>{validityLabel}</strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('PartialAcceptance')}
              </span>
              <strong>{partialAcceptanceLabel}</strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('Visibility')}
              </span>
              <strong>{t('AllPartners')}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="alert alert-warning alert-icon mt-3">
        <em className="icon ni ni-alert-circle" />
        <p className="mb-0 fs-13px">
          {isSellOffer
            ? t('SharesReservedWarning', { count: formatNumber(sharesCount) })
            : t('PublishOfferWarning')}
        </p>
      </div>
    </div>
  );
};

export default PublishTradingOfferModalStep1;
